<template>
  <b-modal
    ref="modal"
    :title="modalTitle"
    hide-footer
  >
    <b-row>
      <b-col cols="7">
        <validation-observer ref="tagForm">
          <b-form @submit.prevent="save">
            <validation-provider
              #default="{ errors }"
              name="tag name"
              rules="required"
            >
              <b-form-group
                label="Tag"
                label-for="tag-name"
              >
                <b-form-input
                  id="tag-name"
                  v-model="local.name"
                  placeholder="Tag Name"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group
              label="Color"
              label-for="tag-color"
            >
              <div class="d-flex align-items-center">
                <b-form-input
                  id="tag-color"
                  v-model="local.color"
                  type="text"
                  placeholder="#rrggbb or color name"
                />
                <input
                  v-model="local.color"
                  type="color"
                  class="ml-2"
                >
              </div>
            </b-form-group>

            <b-form-group
              label="Description"
              label-for="tag-description"
            >
              <b-form-input
                id="tag-description"
                v-model="local.description"
                type="text"
                placeholder="Optional description"
              />
            </b-form-group>

            <div class="d-flex justify-content-end mt-3">
              <b-button
                variant="secondary"
                @click="close"
              >{{
                $t("Cancel")
              }}</b-button>
              <b-button
                class="ml-2"
                type="submit"
                variant="primary"
                :disabled="isSaving"
              >
                {{ $t("Save") }}
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-col>

      <b-col
        cols="5"
        class="d-flex align-items-center justify-content-center"
      >
        <div>
          <label class="d-block text-muted">{{ $t("Preview") }}</label>
          <tag-badge
            :name="local.name || 'Tag'"
            :color="local.color || '#666'"
            :description="local.description || ''"
            size="default"
          />
        </div>
      </b-col>
    </b-row>

    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="close"
      >
        {{ $t("Cancel") }}</b-button>

      <b-button
        class="ml-2"
        variant="primary"
        :disabled="isSaving"
        @click="save"
      >
        {{ $t("Save") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import useApollo from '@/plugins/graphql/useApollo'
import TagBadge from './TagBadge.vue'

export default {
  name: 'AddEditTag',
  components: {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    TagBadge,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      local: {
        id: null, name: '', color: '', description: '',
      },
      isEdit: false,
      isSaving: false,
    }
  },
  computed: {
    modalTitle() {
      return this.isEdit ? this.$t('Edit Tag') : this.$t('Add Tag')
    },
    currentTags() {
      return this.tags?.map(tag => tag.name.toLowerCase()) || []
    },
  },
  methods: {
    open(tag) {
      if (tag) {
        this.local = { ...tag }
        this.isEdit = true
      } else {
        this.local = {
          id: null, name: '', color: '#666666', description: '',
        }
        this.isEdit = false
      }
      this.$refs.modal.show()
    },
    close() {
      this.$refs.modal.hide()
    },
    save() {
      this.$refs.tagForm.validate().then(valid => {
        if (valid) {
          // basic validation
          if (!this.local.name) return
          if (this.isSaving) return
          this.isSaving = true
          const projectUid = this.$store.state.project.selectedProject
          if (this.isEdit) {
            // update existing tag
            useApollo.project
              .updateTag({
                projectUid,
                id: this.local.id,
                name: this.local.name,
                color: this.local.color,
                description: this.local.description,
              })
              .then(resp => {
                const data = resp?.data?.updateTag?.data
                this.$emit(
                  'saved',
                  data ? ({ ...data }) : ({ ...this.local }),
                )
                this.close()
              })
              .catch(err => {
                console.error('Error updating tag', err)
              })
              .finally(() => {
                this.isSaving = false
              })
          } else {
            // create new tag
            useApollo.project
              .createTag({
                projectUid,
                name: this.local.name,
                color: this.local.color,
                description: this.local.description,
              })
              .then(resp => {
                const data = resp?.data?.createTag?.data
                this.$emit(
                  'saved',
                  data ? ({ ...data }) : ({ ...this.local }),
                )
                this.close()
              })
              .catch(error => {
                this.showErrorMessage(error.graphQLErrors?.[0]?.extensions.validation['input.tag'][0])
              })
              .finally(() => {
                this.isSaving = false
              })
          }
        }
      })
    },
  },
}
</script>

<style scoped>
.ml-2 {
  margin-left: 0.5rem;
}
</style>
